import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Users, Bus, Ticket, Package, UserCog, LogOut, X, ScanLine, BarChart3, Luggage, TrendingDown, Receipt
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/clientes', label: 'Clientes', icon: Users },
  { path: '/viagens', label: 'Viagens', icon: Bus },
  { path: '/reservas', label: 'Reservas', icon: Ticket },
  { path: '/bagagens', label: 'Bagagem', icon: Luggage },
  { path: '/encomendas', label: 'Encomendas', icon: Package },
];

const roleLabel = { admin: 'Administrador', operador: 'Operador', financeiro: 'Financeiro' };

function NavLink({ to, icon: Icon, label, isActive, onClick, testId }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
        isActive
          ? 'bg-white text-[#0147a0] shadow-md font-bold'
          : 'text-blue-100/80 hover:bg-white/10 hover:text-white'
      }`}
    >
      <Icon className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? 'text-[#0147a0]' : 'text-blue-200/70 group-hover:text-white'}`} />
      <span>{label}</span>
    </Link>
  );
}

export default function Sidebar({ onClose }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const initials = user?.nome?.split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase() || 'U';

  return (
    <aside
      className="w-64 h-screen flex flex-col shadow-2xl"
      style={{ background: 'linear-gradient(180deg, #0147a0 0%, #003375 60%, #001e45 100%)' }}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <img
            src="/logotipo_gugu.png"
            alt="Gugu Turismo"
            className="w-10 h-10 rounded-xl object-cover flex-shrink-0 shadow-md"
          />
          <div className="leading-none">
            <p className="text-white font-bold text-sm tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Gugu Turismo</p>
            <p className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: '#f9c900' }}>Sistema</p>
          </div>
        </div>
        <button onClick={onClose} className="lg:hidden text-white/50 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors" data-testid="sidebar-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-5 px-3 space-y-0.5 overflow-y-auto">
        <p className="text-blue-200/60 text-[10px] font-bold uppercase tracking-widest px-3 mb-3">Menu Principal</p>

        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            icon={item.icon}
            label={item.label}
            isActive={pathname === item.path || pathname.startsWith(item.path + '/')}
            onClick={onClose}
            testId={`nav-${item.path.replace('/', '')}`}
          />
        ))}

        {(user?.role === 'admin' || user?.role === 'financeiro') && (
          <>
            <div className="h-px bg-white/10 my-4 mx-1" />
            <p className="text-blue-200/60 text-[10px] font-bold uppercase tracking-widest px-3 mb-3">Análises</p>
            <NavLink
              to="/relatorios"
              icon={BarChart3}
              label="Relatórios"
              isActive={pathname === '/relatorios'}
              onClick={onClose}
              testId="nav-relatorios"
            />
            <NavLink
              to="/despesas"
              icon={TrendingDown}
              label="Despesas"
              isActive={pathname === '/despesas'}
              onClick={onClose}
              testId="nav-despesas"
            />
            <NavLink
              to="/contas-pagar"
              icon={Receipt}
              label="Contas a Pagar"
              isActive={pathname === '/contas-pagar'}
              onClick={onClose}
              testId="nav-contas-pagar"
            />
          </>
        )}

        {(user?.role === 'admin' || user?.role === 'operador') && (
          <>
            <div className="h-px bg-white/10 my-4 mx-1" />
            <p className="text-blue-200/60 text-[10px] font-bold uppercase tracking-widest px-3 mb-3">Operações</p>
            <NavLink
              to="/leitor-qrcode"
              icon={ScanLine}
              label="Leitor QR Code"
              isActive={pathname === '/leitor-qrcode'}
              onClick={onClose}
              testId="nav-leitor-qrcode"
            />
          </>
        )}

        {user?.role === 'admin' && (
          <>
            <div className="h-px bg-white/10 my-4 mx-1" />
            <p className="text-blue-200/60 text-[10px] font-bold uppercase tracking-widest px-3 mb-3">Administração</p>
            <NavLink
              to="/usuarios"
              icon={UserCog}
              label="Usuários"
              isActive={pathname === '/usuarios'}
              onClick={onClose}
              testId="nav-usuarios"
            />
          </>
        )}
      </nav>

      {/* User info */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3 px-1">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ring-2 ring-white/20"
            style={{ background: 'linear-gradient(135deg, #d72d21, #ff5a4a)' }}
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-semibold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{user?.nome || 'Usuário'}</p>
            <p className="text-blue-200/60 text-xs">{roleLabel[user?.role] || user?.role}</p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-2 text-blue-100/60 hover:text-white text-xs w-full py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 group"
        >
          <LogOut className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          Sair da conta
        </button>
      </div>
    </aside>
  );
}
